import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// SECURITY (2026-09-25): both customer AND admin auth are now httpOnly-
// cookie only — there is no token to attach as a header anywhere in this
// file anymore. The cookie rides along automatically via the global
// axios.defaults.withCredentials = true (set in App.js).

// Get server time in Brunei timezone
export const getServerTime = async () => {
  const response = await axios.get(`${API}/server-time`);
  return response.data;
};

export const bookingsAPI = {
  checkAvailability: async (locationId, date, timeSlot) => {
    const response = await axios.get(
      `${API}/availability/${locationId}/${date}/${timeSlot}`
    );
    return response.data;
  },

  // One request for the whole day's availability (all 24 hourly slots),
  // instead of one request per visible slot — used by both the Book Court
  // flow and the public Check Availability page.
  checkAvailabilityBatch: async (locationId, date) => {
    const response = await axios.get(
      `${API}/availability-batch/${locationId}/${date}`
    );
    return response.data;
  },

  createBooking: async (bookingData) => {
    const response = await axios.post(`${API}/bookings`, bookingData);
    return response.data;
  },

  getUserBookings: async (userId) => {
    const response = await axios.get(`${API}/bookings/user/${userId}`);
    return response.data;
  },

  getGuestBookings: async (phone) => {
    const response = await axios.get(`${API}/bookings/guest/${phone}`);
    return response.data;
  },

  getBooking: async (bookingId) => {
    const response = await axios.get(`${API}/bookings/${bookingId}`);
    return response.data;
  },

  cancelBooking: async (bookingId) => {
    const response = await axios.post(  // FIXED: Changed from PUT to POST (customer endpoint uses POST)
      `${API}/bookings/${bookingId}/cancel`,
      {}
    );
    return response.data;
  },

  processPayment: async (bookingId, paymentData) => {
    const response = await axios.post(`${API}/bookings/${bookingId}/payment`, paymentData);
    return response.data;
  },

  downloadCalendar: async (bookingId) => {
    const response = await axios.get(
      `${API}/bookings/${bookingId}/calendar`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  getEquipment: async () => {
    const response = await axios.get(`${API}/equipment`);
    return response.data;
  },
};

// One request for the whole day's per-hour pricing (all 24 start slots),
// instead of one request per visible slot — used by both the Book Court
// flow and the public Check Availability page.
export const pricingAPI = {
  calculateBatch: async (locationId, date, sport, duration = 1, quantity = 1) => {
    const response = await axios.get(`${API}/pricing/calculate-batch`, {
      params: { location_id: locationId, date, sport, duration, quantity },
    });
    return response.data;
  },
};

// Locations rarely change (BBA has a small, static set of branches), yet
// every customer page (Home, Bookings, BookingFlow) and several admin
// pages independently fetched the full list on every mount. A short-TTL
// module-level cache avoids the redundant network round-trip while still
// picking up admin edits quickly (TTL) or immediately (explicit
// invalidation on create/update/delete below).
const LOCATIONS_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
let _locationsListCache = { data: null, ts: 0 };
const _locationByIdCache = new Map(); // id -> { data, ts }

const _invalidateLocationsCache = () => {
  _locationsListCache = { data: null, ts: 0 };
  _locationByIdCache.clear();
};

export const locationsAPI = {
  getLocations: async () => {
    const now = Date.now();
    if (_locationsListCache.data && (now - _locationsListCache.ts) < LOCATIONS_CACHE_TTL_MS) {
      return _locationsListCache.data;
    }
    const response = await axios.get(`${API}/locations`);
    _locationsListCache = { data: response.data, ts: now };
    return response.data;
  },
  
  getAllLocations: async () => {
    const response = await axios.get(`${API}/locations/all`);
    return response.data;
  },
  
  getLocation: async (locationId) => {
    const now = Date.now();
    const cached = _locationByIdCache.get(locationId);
    if (cached && (now - cached.ts) < LOCATIONS_CACHE_TTL_MS) {
      return cached.data;
    }
    const response = await axios.get(`${API}/locations/${locationId}`);
    _locationByIdCache.set(locationId, { data: response.data, ts: now });
    return response.data;
  },
  
  createLocation: async (locationData) => {
    const response = await axios.post(`${API}/locations`, locationData);
    _invalidateLocationsCache();
    return response.data;
  },
  
  updateLocation: async (locationId, locationData) => {
    const response = await axios.put(`${API}/locations/${locationId}`, locationData);
    _invalidateLocationsCache();
    return response.data;
  },
  
  deleteLocation: async (locationId) => {
    const response = await axios.delete(`${API}/locations/${locationId}`);
    _invalidateLocationsCache();
    return response.data;
  }
};

export const adminAPI = {
  login: async (email, password) => {
    const response = await axios.post(`${API}/admin/login`, { email, password });
    return response.data;
  },

  verifyMFA: async (mfaToken, otp) => {
    const response = await axios.post(`${API}/admin/verify-mfa`, { 
      mfa_token: mfaToken, 
      otp 
    });
    return response.data;
  },

  resendMFA: async (mfaToken) => {
    const response = await axios.post(`${API}/admin/resend-mfa`, { 
      mfa_token: mfaToken 
    });
    return response.data;
  },

  requestPasswordReset: async (email) => {
    const response = await axios.post(`${API}/admin/forgot-password`, { email });
    return response.data;
  },

  verifyResetOtp: async (resetToken, otp) => {
    const response = await axios.post(`${API}/admin/verify-reset-otp`, {
      reset_token: resetToken,
      otp,
    });
    return response.data;
  },

  resendResetOtp: async (resetToken) => {
    const response = await axios.post(`${API}/admin/resend-reset-otp`, {
      reset_token: resetToken,
    });
    return response.data;
  },

  resetPassword: async (authToken, newPassword) => {
    const response = await axios.post(`${API}/admin/reset-password`, {
      reset_authorization_token: authToken,
      new_password: newPassword,
    });
    return response.data;
  },

  getDashboardStats: async () => {
    const response = await axios.get(`${API}/admin/dashboard/stats`);
    return response.data;
  },

  getPendingBookings: async (dateFrom, dateTo) => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    const response = await axios.get(`${API}/admin/bookings/pending?${params.toString()}`);
    return response.data;
  },

  assignCourts: async (bookingId, courtsAssigned, discountType = null, discountValue = 0, discountReason = '') => {
    // Build discount object if discount is applied
    let discount = null;
    if (discountType && discountType !== 'none' && discountValue > 0) {
      discount = {
        type: discountType,
        value: parseFloat(discountValue),
        reason: discountReason || 'No reason provided'
      };
    }
    
    const response = await axios.post(
      `${API}/admin/bookings/assign-courts`,
      { 
        booking_id: bookingId, 
        courts_assigned: courtsAssigned,
        discount: discount
      }
    );
    return response.data;
  },

  cancelBooking: async (bookingId) => {
    const response = await axios.put(`${API}/admin/bookings/${bookingId}/cancel`, {});
    return response.data;
  },

  // PR-2 pay-later staff actions
  markBookingPaid: async (bookingId, payload = {}) => {
    const response = await axios.post(`${API}/admin/bookings/${bookingId}/mark-paid`, payload);
    return response.data;
  },

  releaseHeldBooking: async (bookingId, payload = {}) => {
    const response = await axios.post(`${API}/admin/bookings/${bookingId}/release`, payload);
    return response.data;
  },

  resendPaymentReminder: async (bookingId) => {
    const response = await axios.post(`${API}/admin/bookings/${bookingId}/resend-payment-reminder`, {});
    return response.data;
  },

  getAwaitingPaymentBookings: async (dateFrom, dateTo) => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    const response = await axios.get(`${API}/admin/bookings/awaiting-payment?${params.toString()}`);
    return response.data;
  },

  getConfirmedBookings: async (dateFrom, dateTo) => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    const response = await axios.get(`${API}/admin/bookings/confirmed?${params.toString()}`);
    return response.data;
  },

  // Unified admin bookings feed (excludes PENDING_ALLOCATION, which lives on Pending Requests).
  // Server-side filtered, sorted (most recent first), and paginated.
  getAllAdminBookings: async ({
    dateFrom = '', dateTo = '', status = '', source = '',
    phone = '', bookingRef = '', email = '', page = 1, limit = 100,
  } = {}) => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (status) params.append('status', status);
    if (source) params.append('source', source);
    if (phone) params.append('phone', phone);
    if (bookingRef) params.append('booking_ref', bookingRef);
    if (email) params.append('email', email);
    params.append('page', page);
    params.append('limit', limit);
    const response = await axios.get(`${API}/admin/bookings/all?${params.toString()}`);
    return response.data;
  },

  getBookingTransaction: async (bookingId) => {
    const response = await axios.get(`${API}/admin/bookings/${bookingId}/transaction`);
    return response.data;
  },

  // Booking Management
  createManualBooking: async (payload) => {
    const response = await axios.post(`${API}/admin/bookings/manual`, payload);
    return response.data;
  },

  // Customer Management
  getCustomers: async () => {
    const response = await axios.get(`${API}/admin/customers`);
    return response.data;
  },

  getCustomerDetails: async (customerId) => {
    const response = await axios.get(`${API}/admin/customers/${customerId}`);
    return response.data;
  },

  // Staff Management
  getStaff: async () => {
    const response = await axios.get(`${API}/admin/staff`);
    return response.data;
  },

  getStaffMember: async (staffId) => {
    const response = await axios.get(`${API}/admin/staff/${staffId}`);
    return response.data;
  },

  createStaff: async (staffData) => {
    const response = await axios.post(`${API}/admin/staff`, staffData);
    return response.data;
  },

  updateStaff: async (staffId, staffData) => {
    const response = await axios.put(`${API}/admin/staff/${staffId}`, staffData);
    return response.data;
  },

  deleteStaff: async (staffId) => {
    const response = await axios.delete(`${API}/admin/staff/${staffId}`);
    return response.data;
  },

  getPricingRules: async (locationId) => {
    const response = await axios.get(`${API}/admin/pricing/${locationId}`);
    return response.data;
  },

  createPricingRule: async (rule) => {
    const response = await axios.post(`${API}/admin/pricing`, rule);
    return response.data;
  },

  updatePricingRule: async (ruleId, rule) => {
    const response = await axios.put(`${API}/admin/pricing/${ruleId}`, rule);
    return response.data;
  },

  deletePricingRule: async (ruleId) => {
    const response = await axios.delete(`${API}/admin/pricing/${ruleId}`);
    return response.data;
  }
};

export const seedData = async () => {
  const response = await axios.post(`${API}/seed-data`);
  return response.data;
};


// Security API (MFA Policy Management)
export const securityAPI = {
  getMFAPolicies: async () => {
    const response = await axios.get(`${API}/admin/security/mfa-policy`);
    return response.data;
  },

  updateMFAPolicy: async (roleName, mfaEnabled) => {
    const response = await axios.patch(
      `${API}/admin/security/mfa-policy/${roleName}`,
      { mfa_enabled: mfaEnabled }
    );
    return response.data;
  }
};

// ==================== BRUNEI HOLIDAYS API ====================
// Backed by /api/admin/holidays/* — used by Settings → Holidays page.
export const holidaysAPI = {
  list: async ({ year, activeOnly = false } = {}) => {
    const params = {};
    if (year) params.year = year;
    if (activeOnly) params.active_only = true;
    const response = await axios.get(`${API}/admin/holidays`, { params });
    return response.data;
  },

  check: async (date) => {
    const response = await axios.get(`${API}/admin/holidays/check/${date}`);
    return response.data;
  },

  create: async (payload) => {
    const response = await axios.post(`${API}/admin/holidays`, payload);
    return response.data;
  },

  update: async (id, payload) => {
    const response = await axios.put(`${API}/admin/holidays/${id}`, payload);
    return response.data;
  },

  remove: async (id, { hard = false } = {}) => {
    const response = await axios.delete(`${API}/admin/holidays/${id}`, {
      params: hard ? { hard: true } : {},
    });
    return response.data;
  },

  importPreset: async (year) => {
    const response = await axios.post(`${API}/admin/holidays/import-preset/${year}`, {});
    return response.data;
  },

  duplicateYear: async (fromYear, toYear) => {
    const response = await axios.post(
      `${API}/admin/holidays/duplicate-year`,
      {},
      { params: { from_year: fromYear, to_year: toYear } }
    );
    return response.data;
  },
};


// ==================== BOOKING SERIES (RECURRING) ====================

export const seriesAPI = {
  previewSeries: async (payload) => {
    const res = await axios.post(`${API}/admin/bookings/series/preview`, payload);
    return res.data;
  },
  createSeries: async (payload) => {
    const res = await axios.post(`${API}/admin/bookings/series`, payload);
    return res.data;
  },
  listSeries: async (params = {}) => {
    const res = await axios.get(`${API}/admin/bookings/series`, { params });
    return res.data;
  },
  getSeries: async (seriesId) => {
    const res = await axios.get(`${API}/admin/bookings/series/${seriesId}`);
    return res.data;
  },
  cancelSeries: async (seriesId, payload = {}) => {
    const res = await axios.post(`${API}/admin/bookings/series/${seriesId}/cancel`, payload);
    return res.data;
  },
};
